import React, { useMemo } from 'react';
import { Box, Typography, Divider, Tooltip, tooltipClasses } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import CategoricalBarChart from 'components/CategoricalBarChart';
import CircularLoader from 'components/CircularLoader';

import icn_linebar_chart from 'assets/images/icons/icn_linebar_chart.png';
import { ReactComponent as Clock } from 'assets/images/icons/icn_display_clock.svg';
import icn_info_solid from 'assets/images/icons/icn_Info_solid.svg';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#4D4D4D',
    color: '#FFFFFF',
    maxWidth: 550,
    fontSize: theme.typography.pxToRem(12),
    padding: '16px 20px',
    borderRadius: '8px',
    lineHeight: '1.5',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
  },
}));

const TooltipContent = () => (
  <Box>
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Good — &lt; 10% of Devices Degraded</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px' }}>
        The device fleet is healthy. CPU and memory utilization are well within operating limits.
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Warning — 10% ≤ Devices Degraded &lt; 20%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        A growing share of devices is hitting resource limits. Management responsiveness may dip on affected nodes.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Investigate systemic causes such as broadcast storms or misconfigured policies.</li>
        <li>Disable non-essential, processor-intensive features on affected devices.</li>
      </Box>
    </Box>

    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Critical — ≥ 20% of Devices Degraded</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        Widespread resource exhaustion may make devices unresponsive or cause spontaneous reboots.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Clear MAC/ARP tables if a broadcast storm or loop is suspected.</li>
        <li>Apply stable firmware patches for known memory-leak issues.</li>
      </Box>
    </Box>
  </Box>
);

const SystemPerformanceBottleneck = ({ data, loading, isEmpty, onClick }) => {
  const theme = useTheme();
  const handleOpenDrawer = !isEmpty ? onClick : undefined;

  const getStatus = (ratio) => {
    if (ratio < 10) return { label: 'Good', color: '#4CAF50' };
    if (ratio < 20) return { label: 'Warning', color: '#FF9800' };
    return { label: 'Critical', color: '#F44336' };
  };

  const allOnlineDevice = data ? data.allOnlineDevice : 0;
  const bottleneckDevice = data ? data.bottleneckDevice : 0;
  const degradedRatio = allOnlineDevice > 0 ? (bottleneckDevice / allOnlineDevice) * 100 : 0;
  const status = data ? getStatus(degradedRatio) : { label: 'Good', color: '#4CAF50' };
  const avgLoad = data ? data.avg : 0;
  const threshold = data ? data.threshold : 70;

  const chartData = useMemo(() => {
    if (!data) return null;
    return {
      categories: ['10%', '20%', '30%', '40%', '50%', '60%', '70%', '80%', '90%', '100%'],
      seriesData: data.series.map((val, idx) => {
        return { value: val, itemStyle: { color: '#4C6EF5' } };
      })
    };
  }, [data]);

  const tooltipOption = useMemo(() => ({
    show: true,
    trigger: 'item',
    backgroundColor: 'rgba(55, 55, 55, 0.9)',
    borderColor: 'transparent',
    padding: [8, 12],
    borderRadius: 4,
    textStyle: { color: '#fff' },
    formatter: (params) => {
      return `
        <div style="font-size: 12px; line-height: 1.6;">
          <div style="opacity: 0.9;">Load Level: ${params.name}</div>
          <div>Device Count : <span style="font-weight: bold;">${params.value}</span></div>
        </div>
      `;
    }
  }), []);

  return (
    <Box
      sx={{
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        p: 2,
        bgcolor: 'white',
        mb: 3,
        cursor: isEmpty ? 'default' : 'pointer',
        '&:hover': handleOpenDrawer ? {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        } : {}
      }}
      onClick={handleOpenDrawer}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={icn_linebar_chart}
            sx={{ width: '18px', height: '18px', mr: 1 }}
          />
          <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '15px', mr: 1 }}>
            System Performance Bottleneck
          </Typography>
          <HtmlTooltip title={<TooltipContent />} placement="auto">
            <Box
              component="img"
              src={icn_info_solid}
              sx={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
          </HtmlTooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <Clock />
          <Typography
            sx={{
              marginLeft: '5px',
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[2],
            }}
          >
            Last 24 Hours
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularLoader />
        </Box>
      ) : isEmpty ? (
        <Box sx={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: theme.palette.primary.greyThird, fontSize: theme.typography.customFontSize[2] }}>
            No data available
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ width: { xs: '100%', md: '25%' }, pr: { xs: 0, md: 4 }, mb: { xs: 3, md: 0 } }}>
            <Typography sx={{ color: '#1C1C1E', fontWeight: 'bold', fontSize: '13px', mb: 1 }}>Performance Compliance</Typography>
            <Typography sx={{ fontSize: '35px', fontWeight: 'bold', color: status.color, lineHeight: 1.2, mb: 2 }}>
              {status.label}
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#1C1C1E' }}>Average Load:</Typography>
                <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>{avgLoad}%</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#1C1C1E' }}>Threshold:</Typography>
                <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>{threshold}%</Typography>
              </Box>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mr: 4, borderColor: '#EDF2F7' }} />

          <Box sx={{ flex: 1, width: '100%' }}>
            <CategoricalBarChart chartData={chartData} height="250px" tooltipCustomOption={tooltipOption} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SystemPerformanceBottleneck;